import {
  JobV2,
  JobPayment,
  JobPaymentResult,
  JobServiceLineItem,
} from "../@types/job";
import { PaymentStatus, PaymentType } from "../@types/enums";
import {
  isLessThan,
  calculateJob,
  createDecimalValue,
  toDecimal,
  isGreaterThanOrEqual,
} from "./math";
import { generateDefaultServiceLineItem } from "../_constants/job";
import { User, UserProcessingFee } from "../@types/user";
import { authenticatedFetch } from "../_utils/authenticatedFetch";

/**
 * Processes a payment for a job and calculates the updated payment status
 * @returns {JobPaymentResult} Updated payment details including remaining balance and payment status
 */

export const processJobPayment = (
  user: User,
  job: JobV2,
  newPayment: JobPayment,
  processingFeeUsed: UserProcessingFee | null
): JobPaymentResult => {
  const { payments, paymentStatus } = job;

  let newProcessingFeeLineItem: JobServiceLineItem | null = null;
  // Validate payment status exists
  if (!paymentStatus) {
    throw Error("Payment status is required");
  }

  // Merge new payment with existing payments
  const updatedPayments = [...payments, newPayment];

  // Sum all payments including the new one
  const totalPayments = calculateJob.totalPayments(updatedPayments).raw();

  // Create new processing fee line item if processing fee exists on new payment
  if (newPayment.processingFee) {
    newProcessingFeeLineItem = generateDefaultServiceLineItem();

    let paymentCount = 1;

    if (newPayment.type === PaymentType.Partial) {
      const numOfPartialPayments = updatedPayments.filter(
        (payment) => payment.type === PaymentType.Partial
      ).length;
      paymentCount = numOfPartialPayments;
    }

    const serviceName = `${processingFeeUsed?.name} for ${
      newPayment.type
    } Payment${
      newPayment.type === PaymentType.Partial ? ` #${paymentCount}` : ""
    }`;

    newProcessingFeeLineItem.service = serviceName;
    newProcessingFeeLineItem.price = newPayment.processingFee;
    newProcessingFeeLineItem.notes = JSON.stringify({
      ...processingFeeUsed,
      type: "ON_PAYMENT",
      linkedPaymentId: newPayment.uid,
    });
  }

  const lineItems = [...job.lineItems, newProcessingFeeLineItem].filter(
    Boolean
  );

  const calculations = {
    // Basic totals
    subtotal: calculateJob.subtotal(lineItems).raw(),
    totalDiscount: calculateJob.totalDiscount(lineItems).raw(),
    subtotalAfterDiscounts: calculateJob
      .subtotalAfterDiscounts(lineItems)
      .raw(),

    // Tax calculations
    totalTax: calculateJob.tax(lineItems, job.taxRate, job.includeTax).raw(),

    // Final totals
    grandTotal: calculateJob
      .grandTotal(lineItems, job.taxRate, job.includeTax)
      .raw(),

    // Deposit and balance
    totalDeposit: calculateJob.totalDeposit(lineItems).raw(),
    remainingBalance: calculateJob
      .remainingBalance(lineItems, job.taxRate, updatedPayments, job.includeTax)
      .raw(),
  };

  const updatedJob = {
    ...job,
    ...calculations,
    lineItems,
    payments: updatedPayments,
  };

  // handles determining if remaining balance is within 0.01 epsilon
  const invoice = calculateJob.asPerClientFacingInvoice(updatedJob);
  const { remainingBalance: remainingBalanceClientFacing } = invoice;
  const { remainingBalance: remainingBalancePrecision } = calculations;

  // Track payment status changes and potential errors
  let newPaymentStatus: PaymentStatus = paymentStatus;
  let error: string | undefined = undefined;

  // Determine payment status based on remaining balance:
  if (isLessThan(remainingBalanceClientFacing, 0)) {
    // Case 1: Overpayment - Set error and mark as paid
    error = `Overpayment detected. Remaining balance is negative by ${createDecimalValue(
      Math.abs(remainingBalanceClientFacing)
    ).formatted()}.`;
    newPaymentStatus = PaymentStatus.PaidInFull;
  } else if (toDecimal(remainingBalanceClientFacing).isZero()) {
    // Case 2: Exact payment - Update payment type and status
    updatedPayments[updatedPayments.length - 1].type = PaymentType.Full;
    newPaymentStatus = PaymentStatus.PaidInFull;
  } else if (
    newPayment.type === PaymentType.Deposit &&
    isGreaterThanOrEqual(totalPayments, job.totalDeposit)
  ) {
    // Case 3: Full deposit received
    newPaymentStatus = PaymentStatus.DepositReceived;
  } else if (
    newPayment.type === PaymentType.Partial ||
    isLessThan(totalPayments, job.grandTotal)
  ) {
    // Case 4: Partial payment received
    newPaymentStatus = PaymentStatus.PartiallyPaid;
  }

  // Record any payment errors
  if (error) {
    newPayment.error = error;
    authenticatedFetch("/api/email/errorNotification", {
      method: "POST",
      body: JSON.stringify({
        userName: user.settings.name,
        userId: user.uid,
        error:
          `${error}` +
          `jobUid: ${job.uid} userId: ${user.uid} paymentUid: ${newPayment.uid}`,
      }),
    });
  }

  return {
    newProcessingFeeLineItem,
    newPaymentStatus,
    updatedPayments,
    remainingBalance: remainingBalancePrecision,
  };
};
